import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { FilterBar } from '../../components/FilterBar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Pill, type PillTone } from '../../components/Pill';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import {
  dashboardApi,
  DashboardApiError,
  type DashboardReconciliationStatus,
} from '../../lib/apiClient';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import { OperationsHeader } from '../operations/OperationsHeader';
import {
  PaymentOperationsPanel,
  PaymentReviewQueue,
} from './PaymentOperationsPanel';

const STATUSES = [
  'late_payment',
  'conflict',
  'mismatch',
  'unsettled',
  'pending',
  'matched',
  'rejected',
  'voided',
  'missing',
] as const satisfies readonly DashboardReconciliationStatus[];

const STATUS_LABELS: Record<DashboardReconciliationStatus, string> = {
  late_payment: 'ชำระล่าช้า',
  conflict: 'ข้อมูลขัดแย้ง',
  mismatch: 'ยอดไม่ตรง',
  unsettled: 'ยังไม่รับชำระ',
  pending: 'รอตรวจสอบ',
  matched: 'ตรงกัน',
  rejected: 'ปฏิเสธ',
  voided: 'ยกเลิก',
  missing: 'ไม่พบข้อมูล',
};

const STATUS_TONES: Record<DashboardReconciliationStatus, PillTone> = {
  late_payment: 'warn',
  conflict: 'danger',
  mismatch: 'danger',
  unsettled: 'warn',
  pending: 'primary',
  matched: 'ok',
  rejected: 'danger',
  voided: 'neutral',
  missing: 'danger',
};

const PROBE_OBSERVED_LABELS: Record<string, string> = {
  payment_observed: 'SCB เห็นรายการชำระแล้ว',
  verification_pending: 'SCB ยังไม่เห็นรายการชำระ',
};

const PROBE_ERROR_LABELS: Record<string, string> = {
  authentication_failed: 'SCB ปฏิเสธการยืนยันตัวตน (HTTP 401)',
  forbidden: 'SCB ไม่อนุญาตให้สอบถามสถานะ (HTTP 403)',
  not_found: 'SCB ไม่พบรายการนี้ (HTTP 404)',
  provider_rejected: 'SCB ปฏิเสธคำขอ (HTTP 400)',
  rate_limited: 'SCB จำกัดอัตราการเรียก (HTTP 429)',
  response_invalid: 'คำตอบจาก SCB ไม่ตรงรูปแบบที่ตกลงไว้',
  transient: 'ติดต่อ SCB ไม่ได้ชั่วคราว',
};

const RUN_TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const SETTLEMENT_IMPORT_MAX_BYTES = 1024 * 1024;

function bangkokDate(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function amount(value: number): string {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 2,
  }).format(value / 100);
}

function signedAmount(value: number): string {
  if (value === 0) return amount(0);
  return `${value > 0 ? '+' : '−'}${amount(Math.abs(value))}`;
}

function dateTime(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('th-TH', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'กรุณาลองใหม่อีกครั้ง';
}

function exportErrorMessage(error: unknown): string {
  if (
    error instanceof DashboardApiError &&
    error.code === 'reconciliation_export_range_too_large'
  ) {
    return 'พบข้อมูลเกิน 5,000 รายการ กรุณาลดช่วงวันที่หรือเลือกสถานะให้แคบลง';
  }
  return errorMessage(error);
}

function newRunKey(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-reconciliation-${random}`.replace(/[^A-Za-z0-9._:-]/g, '-');
}

function newSettlementReconcileKey(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-settlement-reconcile-${random}`.replace(
    /[^A-Za-z0-9._:-]/g,
    '-',
  );
}

function newFileAnomalyResolutionReference(): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `dashboard-file-anomaly-${random}`.replace(/[^A-Za-z0-9._:-]/g, '-');
}

function downloadCsv(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'settlement-reconciliation.csv';
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ReconciliationView() {
  const user = useSession((state) => state.user);
  const shellBranchId = useUi((state) => state.branchId);
  const queryClient = useQueryClient();
  const canAccess = user?.role === 'owner' || user?.role === 'manager';
  const [branchId, setBranchId] = useState('');
  const [from, setFrom] = useState(bangkokDate);
  const [to, setTo] = useState(bangkokDate);
  const [status, setStatus] = useState<DashboardReconciliationStatus | ''>('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [settlementFile, setSettlementFile] = useState<File | null>(null);
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  const [selectedAnomalyAlertId, setSelectedAnomalyAlertId] = useState<
    string | null
  >(null);
  const [anomalyReasons, setAnomalyReasons] = useState<Record<string, string>>(
    {},
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const runIdentity = useRef<{ fingerprint: string; key: string } | null>(null);
  const settlementReconcileIdentity = useRef<{
    fingerprint: string;
    key: string;
  } | null>(null);
  const anomalyResolutionIdentity = useRef<
    Record<string, { fingerprint: string; reference: string }>
  >({});

  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: () => dashboardApi.listBranches(),
    enabled: canAccess,
  });
  const branches = branchesQuery.data ?? [];
  const allowedBranchIds = useMemo(
    () =>
      user
        ? visibleBranchIds(
            user,
            branches.map((branch) => branch.id),
          )
        : [],
    [branches, user],
  );
  const visibleBranches = branches.filter((branch) =>
    allowedBranchIds.includes(branch.id),
  );

  useEffect(() => {
    if (branchId && allowedBranchIds.includes(branchId)) return;
    setBranchId(
      (shellBranchId && allowedBranchIds.includes(shellBranchId)
        ? shellBranchId
        : null) ??
        allowedBranchIds[0] ??
        '',
    );
  }, [allowedBranchIds, branchId, shellBranchId]);

  const listQuery = useInfiniteQuery({
    queryKey: ['payment-reconciliation', branchId, from, to, status],
    queryFn: ({ pageParam }) =>
      dashboardApi.listReconciliation({
        branchId,
        from,
        to,
        ...(status ? { status } : {}),
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled:
      canAccess &&
      Boolean(branchId) &&
      Boolean(from) &&
      Boolean(to) &&
      from <= to,
  });
  const payments = listQuery.data?.pages.flatMap((page) => page.payments) ?? [];
  const summary = listQuery.data?.pages[0]?.summary;

  const detailQuery = useQuery({
    queryKey: ['payment-reconciliation-detail', selectedPaymentId],
    queryFn: () => dashboardApi.getReconciliationPayment(selectedPaymentId!),
    enabled: canAccess && Boolean(selectedPaymentId),
  });

  const anomalyListQuery = useInfiniteQuery({
    queryKey: ['settlement-import-anomalies', selectedImportId, branchId],
    queryFn: ({ pageParam }) =>
      dashboardApi.listSettlementImportAnomalies({
        importId: selectedImportId!,
        branchId,
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: canAccess && Boolean(branchId) && Boolean(selectedImportId),
  });
  const anomalies =
    anomalyListQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const anomalyDetailQuery = useQuery({
    queryKey: [
      'settlement-import-anomaly-detail',
      selectedImportId,
      selectedAnomalyAlertId,
      branchId,
    ],
    queryFn: () =>
      dashboardApi.getSettlementImportAnomaly({
        importId: selectedImportId!,
        alertId: selectedAnomalyAlertId!,
        branchId,
      }),
    enabled:
      canAccess &&
      Boolean(branchId) &&
      Boolean(selectedImportId) &&
      Boolean(selectedAnomalyAlertId),
  });

  const runMutation = useMutation({
    mutationFn: () => {
      const fingerprint = `${branchId}:${from}:${to}`;
      if (runIdentity.current?.fingerprint !== fingerprint) {
        runIdentity.current = { fingerprint, key: newRunKey() };
      }
      return dashboardApi.createReconciliationRun({
        branchId,
        from,
        to,
        idempotencyKey: runIdentity.current.key,
      });
    },
    onSuccess: (result) => setRunId(result.runId),
  });

  const runQuery = useQuery({
    queryKey: ['payment-reconciliation-run', runId],
    queryFn: () => dashboardApi.getReconciliationRun(runId!),
    enabled: canAccess && Boolean(runId),
    refetchInterval: (query) =>
      RUN_TERMINAL.has(query.state.data?.status ?? '') ? false : 1_500,
  });

  useEffect(() => {
    if (!runQuery.data?.status || !RUN_TERMINAL.has(runQuery.data.status))
      return;
    runIdentity.current = null;
    if (runQuery.data.status === 'completed') {
      void queryClient.invalidateQueries({
        queryKey: ['payment-reconciliation'],
      });
    }
  }, [queryClient, runQuery.data?.status]);

  const resolveMutation = useMutation({
    mutationFn: ({ alertId, reason }: { alertId: string; reason: string }) =>
      dashboardApi.resolveReconciliationAlert({ alertId, reason }),
    onSuccess: async (_result, variables) => {
      setReasons((current) => ({ ...current, [variables.alertId]: '' }));
      await Promise.all([
        detailQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['payment-reconciliation'] }),
      ]);
    },
  });

  const probeMutation = useMutation({
    mutationFn: (paymentId: string) =>
      dashboardApi.probeReconciliationPaymentGateway({ paymentId, branchId }),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) =>
      dashboardApi.importReconciliationSettlementCsv({ branchId, file }),
    onSuccess: async (result) => {
      setSelectedImportId(result.importId);
      settlementReconcileIdentity.current = null;
      await queryClient.invalidateQueries({
        queryKey: ['payment-reconciliation'],
      });
    },
  });

  const settlementReconcileMutation = useMutation({
    mutationFn: () => {
      if (!selectedImportId) {
        throw new Error('ไม่พบเลขที่นำเข้าที่จะกระทบยอด');
      }
      const fingerprint = `${selectedImportId}:${branchId}:${from}:${to}`;
      if (settlementReconcileIdentity.current?.fingerprint !== fingerprint) {
        settlementReconcileIdentity.current = {
          fingerprint,
          key: newSettlementReconcileKey(),
        };
      }
      return dashboardApi.reconcileSettlementImport({
        importId: selectedImportId,
        branchId,
        from,
        to,
        idempotencyKey: settlementReconcileIdentity.current.key,
      });
    },
    onSuccess: async () => {
      settlementReconcileIdentity.current = null;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['payment-reconciliation'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['payment-reconciliation-detail'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['settlement-import-anomalies'],
        }),
      ]);
    },
  });

  const anomalyResolveMutation = useMutation({
    mutationFn: ({ alertId, reason }: { alertId: string; reason: string }) => {
      if (!selectedImportId)
        throw new Error('ไม่พบเลขที่นำเข้าของการแจ้งเตือน');
      const fingerprint = `${selectedImportId}:${branchId}:${alertId}:${reason}`;
      if (
        anomalyResolutionIdentity.current[alertId]?.fingerprint !== fingerprint
      ) {
        anomalyResolutionIdentity.current[alertId] = {
          fingerprint,
          reference: newFileAnomalyResolutionReference(),
        };
      }
      return dashboardApi.resolveSettlementImportAnomaly({
        importId: selectedImportId,
        branchId,
        alertId,
        reason,
        resolutionReference:
          anomalyResolutionIdentity.current[alertId]!.reference,
      });
    },
    onSuccess: async (_result, variables) => {
      delete anomalyResolutionIdentity.current[variables.alertId];
      setAnomalyReasons((current) => ({
        ...current,
        [variables.alertId]: '',
      }));
      await Promise.all([
        anomalyDetailQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: ['settlement-import-anomalies'],
        }),
        queryClient.invalidateQueries({ queryKey: ['payment-reconciliation'] }),
      ]);
    },
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      dashboardApi.exportReconciliationCsv({
        branchId,
        from,
        to,
        ...(status ? { status } : {}),
      }),
    onSuccess: downloadCsv,
  });

  useEffect(() => {
    setSelectedImportId(null);
    setSelectedAnomalyAlertId(null);
    anomalyResolutionIdentity.current = {};
    settlementReconcileIdentity.current = null;
    importMutation.reset();
    settlementReconcileMutation.reset();
  }, [branchId, from, to]);

  if (!canAccess) {
    return (
      <div className="bo-fade">
        <OperationsHeader
          title="กระทบยอดชำระ"
          subtitle="ตรวจสอบความตรงกันของรายการชำระเงิน"
          status={{ label: 'จำกัดสิทธิ์ผู้ดูแล', tone: 'neutral' }}
        />
        <Card>
          <EmptyState
            title="ไม่มีสิทธิ์ดูข้อมูลกระทบยอด"
            sub="เฉพาะผู้จัดการหรือเจ้าของเท่านั้น"
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <OperationsHeader
        title="กระทบยอดชำระ"
        subtitle="เทียบยอดจากเครื่อง POS ผู้ให้บริการชำระเงิน และไฟล์ settlement ตามวันธุรกิจของร้าน"
        actions={
          <>
            <Btn
              onClick={() => exportMutation.mutate()}
              disabled={!branchId || from > to || exportMutation.isPending}
            >
              {exportMutation.isPending ? 'กำลังส่งออก…' : 'ส่งออก CSV'}
            </Btn>
            <Btn
              kind="primary"
              onClick={() => runMutation.mutate()}
              disabled={!branchId || from > to || runMutation.isPending}
            >
              {runMutation.isPending ? 'กำลังเริ่ม…' : 'กระทบยอดใหม่'}
            </Btn>
          </>
        }
      />

      <PaymentReviewQueue branchId={branchId} onSelect={setSelectedPaymentId} />
      <FilterBar>
        <div className="w-full">
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="สาขา">
              <Select
                aria-label="สาขา"
                value={branchId}
                onChange={(event) => {
                  setBranchId(event.target.value);
                  setSettlementFile(null);
                  importMutation.reset();
                }}
              >
                {visibleBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.short}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="จากวันที่">
              <Input
                aria-label="จากวันที่"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </Field>
            <Field label="ถึงวันที่">
              <Input
                aria-label="ถึงวันที่"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </Field>
            <Field label="สถานะ">
              <Select
                aria-label="สถานะ"
                value={status}
                onChange={(event) =>
                  setStatus(
                    event.target.value as DashboardReconciliationStatus | '',
                  )
                }
              >
                <option value="">ทุกสถานะ</option>
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {from > to ? (
            <p className="mt-3 text-sm-label text-danger">
              วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด
            </p>
          ) : null}
        </div>
      </FilterBar>

      <Card title="นำเข้า settlement CSV" className="mb-6">
        <div className="grid items-end gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <Field label="ไฟล์ settlement CSV" className="min-w-0">
            <Input
              key={branchId}
              aria-label="ไฟล์ settlement CSV"
              className="w-full min-w-0"
              type="file"
              accept=".csv,text/csv"
              disabled={!branchId || importMutation.isPending}
              onChange={(event) => {
                setSettlementFile(event.target.files?.[0] ?? null);
                importMutation.reset();
              }}
            />
          </Field>
          <Btn
            kind="primary"
            disabled={!branchId || !settlementFile || importMutation.isPending}
            onClick={() => {
              if (settlementFile) importMutation.mutate(settlementFile);
            }}
          >
            {importMutation.isPending ? 'กำลังนำเข้า…' : 'นำเข้า settlement'}
          </Btn>
        </div>
        <p className="mt-2 text-sm-label text-ink-3">
          ขนาดสูงสุด 1 MiB และ 5,000 แถว
          ระบบจะตรวจสอบไฟล์และข้อมูลทั้งหมดอีกครั้ง
        </p>
        {settlementFile ? (
          <p className="mt-2 text-sm-label text-ink-2">
            เลือกแล้ว: {settlementFile.name} (
            {new Intl.NumberFormat('th-TH').format(settlementFile.size)} bytes)
          </p>
        ) : null}
        {settlementFile && settlementFile.size > SETTLEMENT_IMPORT_MAX_BYTES ? (
          <p className="mt-2 text-sm-label text-danger">
            ไฟล์เกิน 1 MiB และระบบจะปฏิเสธการนำเข้า
          </p>
        ) : null}
        {importMutation.isError ? (
          <p role="alert" className="mt-3 text-sm-label text-danger">
            นำเข้า settlement ไม่สำเร็จ: {errorMessage(importMutation.error)}
          </p>
        ) : importMutation.data ? (
          <div
            role="status"
            className="mt-3 rounded-lg border border-line p-3 text-sm-label"
          >
            <div className="font-semibold text-ink">
              {importMutation.data.status === 'replay'
                ? `ไฟล์นี้นำเข้าแล้ว ${importMutation.data.rowCount} แถว`
                : `นำเข้าสำเร็จ ${importMutation.data.rowCount} แถว`}
            </div>
            <div className="mt-1 text-ink-3">
              เลขที่นำเข้า: {importMutation.data.importId}
            </div>
            {selectedImportId ? (
              <div className="mt-3">
                <Btn
                  size="sm"
                  kind="primary"
                  disabled={from > to || settlementReconcileMutation.isPending}
                  onClick={() => settlementReconcileMutation.mutate()}
                >
                  {settlementReconcileMutation.isPending
                    ? 'กำลังกระทบยอดไฟล์…'
                    : 'กระทบยอดไฟล์นี้'}
                </Btn>
              </div>
            ) : null}
          </div>
        ) : null}
        {settlementReconcileMutation.isError ? (
          <p role="alert" className="mt-3 text-sm-label text-danger">
            กระทบยอดไฟล์ไม่สำเร็จ:{' '}
            {errorMessage(settlementReconcileMutation.error)}
          </p>
        ) : settlementReconcileMutation.data ? (
          <div
            role="status"
            className="mt-3 rounded-lg border border-line p-3 text-sm-label"
          >
            <div className="font-semibold text-ink">
              {settlementReconcileMutation.data.status === 'replay'
                ? 'กระทบยอดไฟล์เดิมแล้ว'
                : 'กระทบยอดไฟล์สำเร็จ'}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-ink-2">
              <span>
                ตรงกัน {settlementReconcileMutation.data.counters.matched}
              </span>
              <span>
                ยอดไม่ตรง{' '}
                {settlementReconcileMutation.data.counters.amountMismatch}
              </span>
              <span>
                อ้างอิงไม่ตรง{' '}
                {settlementReconcileMutation.data.counters.referenceMismatch}
              </span>
              <span>
                ซ้ำ {settlementReconcileMutation.data.counters.duplicate}
              </span>
              <span>
                ไม่มีคู่ {settlementReconcileMutation.data.counters.orphan}
              </span>
              <span>
                ขาด settlement{' '}
                {settlementReconcileMutation.data.counters.missingSettlement}
              </span>
              <span>
                แจ้งเตือน {settlementReconcileMutation.data.counters.alerts}
              </span>
            </div>
          </div>
        ) : null}
        {selectedImportId ? (
          <div className="mt-5 border-t border-line pt-4">
            <h3 className="font-bold">ความผิดปกติจากไฟล์นี้</h3>
            {anomalyListQuery.isLoading ? (
              <p className="mt-2 text-ink-3">กำลังโหลดการแจ้งเตือน จากไฟล์…</p>
            ) : anomalyListQuery.isError ? (
              <div className="mt-2">
                <p className="text-danger">
                  โหลดการแจ้งเตือน จากไฟล์ไม่สำเร็จ:{' '}
                  {errorMessage(anomalyListQuery.error)}
                </p>
                <Btn
                  className="mt-2"
                  size="sm"
                  onClick={() => void anomalyListQuery.refetch()}
                >
                  ลองใหม่
                </Btn>
              </div>
            ) : anomalies.length === 0 ? (
              <p className="mt-2 text-ink-3">ไม่พบความผิดปกติจากไฟล์นี้</p>
            ) : (
              <div className="mt-3 space-y-2">
                <DataTable
                  pageSize={Number.MAX_SAFE_INTEGER}
                  mobileCards
                  data={anomalies}
                  onRowClick={(anomaly) =>
                    setSelectedAnomalyAlertId(anomaly.alertId)
                  }
                  columns={[
                    { accessorKey: 'alertId', header: 'เลขที่แจ้งเตือน' },
                    { accessorKey: 'alertType', header: 'ประเภท' },
                    {
                      id: 'row',
                      header: 'แถวในไฟล์',
                      cell: ({ row }) =>
                        row.original.settlementRowId ?? 'ไม่มีแถวในไฟล์',
                    },
                    {
                      id: 'status',
                      header: 'สถานะ',
                      cell: ({ row }) => (
                        <Pill
                          tone={
                            row.original.status === 'resolved' ? 'ok' : 'warn'
                          }
                        >
                          {row.original.status === 'resolved'
                            ? 'ปิดแล้ว'
                            : 'เปิดอยู่'}
                        </Pill>
                      ),
                    },
                  ]}
                />
                {anomalyListQuery.hasNextPage ? (
                  <div className="flex justify-center">
                    <Btn
                      size="sm"
                      disabled={anomalyListQuery.isFetchingNextPage}
                      onClick={() => void anomalyListQuery.fetchNextPage()}
                    >
                      {anomalyListQuery.isFetchingNextPage
                        ? 'กำลังโหลด…'
                        : 'โหลดการแจ้งเตือน เพิ่ม'}
                    </Btn>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </Card>

      {exportMutation.isError ? (
        <Card className="mb-6">
          <p role="alert" className="text-danger">
            ส่งออก CSV ไม่สำเร็จ: {exportErrorMessage(exportMutation.error)}
          </p>
        </Card>
      ) : null}

      {runMutation.isError ? (
        <Card className="mb-6">
          <p className="text-danger">
            เริ่มกระทบยอดไม่สำเร็จ: {errorMessage(runMutation.error)}
          </p>
        </Card>
      ) : runId ? (
        <Card title="สถานะการกระทบยอด" className="mb-6">
          {runQuery.isLoading ? (
            <p className="text-ink-3">กำลังตรวจสอบสถานะ…</p>
          ) : runQuery.isError ? (
            <p className="text-danger">
              โหลดสถานะไม่สำเร็จ: {errorMessage(runQuery.error)}
            </p>
          ) : runQuery.data ? (
            <div className="flex flex-wrap items-center gap-4 text-body">
              <Pill
                tone={
                  runQuery.data.status === 'completed'
                    ? 'ok'
                    : runQuery.data.status === 'failed'
                      ? 'danger'
                      : 'primary'
                }
              >
                {runQuery.data.status === 'completed'
                  ? 'กระทบยอดเสร็จแล้ว'
                  : runQuery.data.status === 'failed'
                    ? 'กระทบยอดไม่สำเร็จ'
                    : 'กำลังประมวลผล'}
              </Pill>
              <span>ตรวจแล้ว {runQuery.data.processedCount}</span>
              <span>ตรงกัน {runQuery.data.matchedCount}</span>
              <span>แจ้งเตือน {runQuery.data.alertCount}</span>
              <span>ยังไม่ยืนยัน {runQuery.data.unverifiedCount}</span>
              {runQuery.data.errorCode ? (
                <span className="text-danger">
                  รหัสข้อผิดพลาด: {runQuery.data.errorCode}
                </span>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}

      {summary ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <div className="text-sm-label text-ink-3">รายการทั้งหมด</div>
            <div className="text-h2 font-bold">{summary.total}</div>
          </Card>
          {(['matched', 'pending', 'mismatch', 'conflict'] as const).map(
            (value) => (
              <Card key={value}>
                <div className="text-sm-label text-ink-3">
                  {STATUS_LABELS[value]}
                </div>
                <div className="text-h2 font-bold">{summary[value]}</div>
              </Card>
            ),
          )}
        </div>
      ) : null}

      {branchesQuery.isLoading || listQuery.isLoading ? (
        <Card>
          <p className="text-ink-3">กำลังโหลดข้อมูลกระทบยอด…</p>
        </Card>
      ) : branchesQuery.isError ? (
        <Card>
          <EmptyState
            title="โหลดข้อมูลสาขาไม่สำเร็จ"
            sub={errorMessage(branchesQuery.error)}
          />
        </Card>
      ) : visibleBranches.length === 0 ? (
        <Card>
          <EmptyState
            title="ไม่พบสาขาที่ได้รับสิทธิ์"
            sub="ติดต่อเจ้าของระบบเพื่อตรวจสอบขอบเขตสาขา"
          />
        </Card>
      ) : listQuery.isError ? (
        <Card>
          <EmptyState
            title="โหลดข้อมูลกระทบยอดไม่สำเร็จ"
            sub={errorMessage(listQuery.error)}
            action={<Btn onClick={() => void listQuery.refetch()}>ลองใหม่</Btn>}
          />
        </Card>
      ) : payments.length === 0 ? (
        <Card>
          <EmptyState
            title="ไม่พบรายการที่ต้องกระทบยอด"
            sub="ลองเปลี่ยนช่วงวันที่หรือสถานะ"
          />
        </Card>
      ) : (
        <>
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={payments}
            onRowClick={(item) => {
              probeMutation.reset();
              setSelectedPaymentId(item.paymentId);
            }}
            columns={[
              {
                accessorKey: 'paymentId',
                header: 'การชำระเงิน',
                cell: ({ row }) => (
                  <div className="min-w-0 break-all">
                    <div>{row.original.paymentId}</div>
                    <div className="mt-1 text-sm-label text-ink-3">
                      เลขที่ขาย: {row.original.saleId ?? '—'}
                    </div>
                  </div>
                ),
              },
              {
                id: 'time',
                header: 'เวลา',
                cell: ({ row }) => dateTime(row.original.createdAt),
              },
              {
                id: 'amount',
                header: 'ยอด',
                cell: ({ row }) => amount(row.original.amountSatang),
              },
              {
                id: 'truth',
                header: 'ผู้ให้บริการ / POS',
                cell: ({ row }) =>
                  `${row.original.gatewayTruth ?? '—'} / ${row.original.projectionTruth ?? '—'}`,
              },
              {
                id: 'status',
                header: 'สถานะ',
                cell: ({ row }) => (
                  <Pill tone={STATUS_TONES[row.original.status]}>
                    {STATUS_LABELS[row.original.status]}
                  </Pill>
                ),
              },
              { accessorKey: 'openAlertCount', header: 'แจ้งเตือน' },
            ]}
          />
          {listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Btn
                onClick={() => void listQuery.fetchNextPage()}
                disabled={listQuery.isFetchingNextPage}
              >
                {listQuery.isFetchingNextPage ? 'กำลังโหลด…' : 'โหลดเพิ่ม'}
              </Btn>
            </div>
          ) : null}
        </>
      )}

      <Modal
        open={Boolean(selectedPaymentId)}
        onClose={() => setSelectedPaymentId(null)}
        title="รายละเอียดการชำระ"
        variant="drawer"
        className="!my-0"
        description="ตรวจสอบยอด เหตุการณ์ และหลักฐานการจับคู่ก่อนปิดรายการผิดปกติ"
      >
        {detailQuery.isLoading ? (
          <p className="text-ink-3">กำลังโหลดรายละเอียด…</p>
        ) : detailQuery.isError ? (
          <EmptyState
            title="โหลดรายละเอียดไม่สำเร็จ"
            sub={errorMessage(detailQuery.error)}
          />
        ) : detailQuery.data ? (
          <div className="space-y-5">
            <PaymentOperationsPanel
              key={selectedPaymentId!}
              paymentId={selectedPaymentId!}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-sm-label text-ink-3">เลขที่ชำระเงิน</div>
                <div className="font-semibold break-all">
                  {detailQuery.data.paymentId}
                </div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">เลขที่ขาย</div>
                <div>{detailQuery.data.saleId ?? '—'}</div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">ยอดชำระ</div>
                <div>{amount(detailQuery.data.amountSatang)}</div>
              </div>
              <div>
                <div className="text-sm-label text-ink-3">อัปเดตล่าสุด</div>
                <div>{dateTime(detailQuery.data.updatedAt)}</div>
              </div>
            </div>
            <div className="rounded-lg border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold">ตรวจสอบกับ SCB</h4>
                  <p className="mt-1 text-sm-label text-ink-3">
                    สอบถาม SCB เพื่อดูว่าติดต่อได้หรือไม่
                    ผลตรวจนี้เป็นการอ่านอย่างเดียว ไม่เปลี่ยนสถานะรายการ
                  </p>
                </div>
                <Btn
                  size="sm"
                  disabled={probeMutation.isPending || !selectedPaymentId}
                  aria-label={`สอบถามสถานะ ${selectedPaymentId ?? ''}`}
                  onClick={() => {
                    if (selectedPaymentId)
                      probeMutation.mutate(selectedPaymentId);
                  }}
                >
                  {probeMutation.isPending ? 'กำลังตรวจ…' : 'สอบถามสถานะ'}
                </Btn>
              </div>
              {probeMutation.isError ? (
                <p role="alert" className="mt-3 text-sm-label text-danger">
                  ตรวจกับ SCB ไม่สำเร็จ:{' '}
                  {probeMutation.error instanceof DashboardApiError &&
                  probeMutation.error.code === 'rate_limited'
                    ? 'เพิ่งตรวจไปเมื่อสักครู่ รออีกนิดแล้วลองใหม่'
                    : errorMessage(probeMutation.error)}
                </p>
              ) : probeMutation.data ? (
                <div className="mt-3 space-y-1 text-sm-label">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill
                      tone={
                        probeMutation.data.observation.kind === 'provider_error'
                          ? 'danger'
                          : probeMutation.data.observation.status ===
                              'payment_observed'
                            ? 'ok'
                            : 'warn'
                      }
                    >
                      {probeMutation.data.observation.kind === 'provider_error'
                        ? (PROBE_ERROR_LABELS[
                            probeMutation.data.observation.code
                          ] ?? probeMutation.data.observation.code)
                        : (PROBE_OBSERVED_LABELS[
                            probeMutation.data.observation.status
                          ] ?? probeMutation.data.observation.status)}
                    </Pill>
                    <span className="text-ink-3">
                      {dateTime(probeMutation.data.probedAt)}
                    </span>
                  </div>
                  <div className="text-ink-2">
                    รหัสการชำระ: {probeMutation.data.attemptId} (
                    {probeMutation.data.attemptStatus})
                  </div>
                  <div className="text-ink-3">
                    อ้างอิง: {probeMutation.data.reference} ·{' '}
                    {probeMutation.data.provider}/
                    {probeMutation.data.environment}
                  </div>
                  {probeMutation.data.observation.kind === 'observed' &&
                  probeMutation.data.observation.providerTransactionId ? (
                    <div className="text-ink-3">
                      รายการผู้ให้บริการ:{' '}
                      {probeMutation.data.observation.providerTransactionId}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div>
              <h4 className="font-bold mb-2">แจ้งเตือน</h4>
              {detailQuery.data.alerts.length === 0 ? (
                <p className="text-ink-3">ไม่มีการแจ้งเตือน</p>
              ) : (
                <div className="space-y-3">
                  {detailQuery.data.alerts.map((alert, index) => {
                    const id = alert.alertId ?? `unknown-${index}`;
                    const open = Boolean(alert.alertId && !alert.resolvedAt);
                    return (
                      <div
                        key={id}
                        className="rounded-lg border border-line p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold">
                            {alert.type ?? 'unknown'}
                          </span>
                          <Pill
                            tone={
                              alert.resolvedAt
                                ? 'ok'
                                : alert.severity === 'critical'
                                  ? 'danger'
                                  : 'warn'
                            }
                          >
                            {alert.resolvedAt ? 'ปิดแล้ว' : 'เปิดอยู่'}
                          </Pill>
                        </div>
                        {open ? (
                          <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                            <Field label="เหตุผลในการปิดการแจ้งเตือน">
                              <Input
                                aria-label={`เหตุผลการปิดการแจ้งเตือน ${alert.alertId}`}
                                maxLength={500}
                                value={reasons[id] ?? ''}
                                onChange={(event) =>
                                  setReasons((current) => ({
                                    ...current,
                                    [id]: event.target.value,
                                  }))
                                }
                                placeholder="ระบุเหตุผลที่ตรวจสอบได้"
                              />
                            </Field>
                            <Btn
                              kind="primary"
                              className="w-full sm:w-auto"
                              disabled={
                                !reasons[id]?.trim() ||
                                resolveMutation.isPending
                              }
                              onClick={() =>
                                resolveMutation.mutate({
                                  alertId: alert.alertId!,
                                  reason: reasons[id]!.trim(),
                                })
                              }
                            >
                              ปิดการแจ้งเตือน
                            </Btn>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
              {resolveMutation.isError ? (
                <p role="alert" className="mt-3 text-sm-label text-danger">
                  ปิดการแจ้งเตือน ไม่สำเร็จ:{' '}
                  {errorMessage(resolveMutation.error)}
                </p>
              ) : null}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                ความพยายามชำระ ({detailQuery.data.attempts.length})
              </h4>
              {detailQuery.data.attempts.length === 0 ? (
                <p className="text-ink-3">ไม่มีความพยายามชำระ</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.attempts.map((attempt, index) => (
                    <div
                      key={attempt.attemptId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {attempt.attemptId ?? 'ไม่ทราบรหัสการชำระ'}
                        </span>
                        <Pill tone="primary">
                          {attempt.status ?? 'unknown'}
                        </Pill>
                      </div>
                      <div className="mt-1 text-ink-2">
                        รายการผู้ให้บริการ:{' '}
                        {attempt.providerTransactionId ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        อ้างอิง:{' '}
                        {attempt.references?.reconciliation ??
                          attempt.references?.primary ??
                          attempt.references?.routing ??
                          '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        อัปเดต: {dateTime(attempt.updatedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                เหตุการณ์ ({detailQuery.data.events.length})
              </h4>
              {detailQuery.data.events.length === 0 ? (
                <p className="text-ink-3">ไม่มีเหตุการณ์</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.events.map((event, index) => (
                    <div
                      key={event.eventId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {event.source ?? 'ไม่ทราบแหล่งที่มา'}
                        </span>
                        <span>{event.processingResult ?? 'ไม่ทราบผล'}</span>
                      </div>
                      <div className="mt-1 text-ink-2">
                        ผลตรวจ: {event.validationResult ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        เกิดเมื่อ:{' '}
                        {dateTime(event.occurredAt ?? event.receivedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-bold mb-2">
                Settlements ({detailQuery.data.settlements.length})
              </h4>
              {detailQuery.data.settlements.length === 0 ? (
                <p className="text-ink-3">ไม่มี settlement</p>
              ) : (
                <div className="space-y-2">
                  {detailQuery.data.settlements.map((settlement, index) => (
                    <div
                      key={settlement.matchId ?? index}
                      className="rounded-lg border border-line p-3 text-sm-label"
                    >
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="font-semibold">
                          {settlement.outcome ?? 'ไม่ทราบผลการจับคู่'}
                        </span>
                        <span>{amount(settlement.settlementAmountSatang)}</span>
                      </div>
                      <div className="mt-1 text-ink-2">
                        รายการผู้ให้บริการ:{' '}
                        {settlement.providerTransactionId ?? '—'}
                      </div>
                      <div className="mt-1 text-ink-3">
                        รับชำระ:{' '}
                        {dateTime(settlement.settledAt ?? settlement.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(selectedImportId && selectedAnomalyAlertId)}
        onClose={() => setSelectedAnomalyAlertId(null)}
        title="รายละเอียดความผิดปกติของไฟล์"
        description="ดูข้อมูลจากไฟล์และผลการจับคู่ แล้วบันทึกเหตุผลเมื่อแก้ไขรายการเรียบร้อย"
        className="w-[min(680px,94vw)]"
      >
        {anomalyDetailQuery.isLoading ? (
          <p className="text-ink-3">กำลังโหลดรายละเอียด…</p>
        ) : anomalyDetailQuery.isError ? (
          <EmptyState
            title="โหลดรายละเอียดการแจ้งเตือน ไม่สำเร็จ"
            sub={errorMessage(anomalyDetailQuery.error)}
          />
        ) : anomalyDetailQuery.data ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-bold">
                {anomalyDetailQuery.data.alert.alertType}
              </span>
              <Pill
                tone={
                  anomalyDetailQuery.data.alert.status === 'resolved'
                    ? 'ok'
                    : 'warn'
                }
              >
                {anomalyDetailQuery.data.alert.status === 'resolved'
                  ? 'ปิดแล้ว'
                  : 'เปิดอยู่'}
              </Pill>
            </div>
            {anomalyDetailQuery.data.settlementRow ? (
              <div className="grid gap-3 rounded-lg border border-line p-3 text-sm-label sm:grid-cols-2">
                <div>
                  <div className="text-ink-3">เลขที่แถว settlement</div>
                  <div className="break-all font-semibold">
                    {anomalyDetailQuery.data.settlementRow.settlementRowId}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">เลขที่รายการผู้ให้บริการ</div>
                  <div>
                    {anomalyDetailQuery.data.settlementRow
                      .providerTransactionIdPresent
                      ? 'มีข้อมูล'
                      : 'ไม่มีข้อมูล'}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">อ้างอิงที่มีในไฟล์</div>
                  <div>
                    {[
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .primary
                        ? 'primary'
                        : null,
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .reconciliation
                        ? 'reconciliation'
                        : null,
                      anomalyDetailQuery.data.settlementRow.referencePresence
                        .routing
                        ? 'routing'
                        : null,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'ไม่มีข้อมูล'}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-ink-3">การแจ้งเตือนนี้ไม่มีแถว settlement</p>
            )}
            {anomalyDetailQuery.data.match ? (
              <div className="space-y-3">
                <div className="grid gap-3 rounded-lg border border-line p-3 text-sm-label sm:grid-cols-2">
                  <div>
                    <div className="text-ink-3">ผลการจับคู่</div>
                    <div className="font-semibold">
                      {anomalyDetailQuery.data.match.outcome}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ยอด settlement</div>
                    <div>
                      {anomalyDetailQuery.data.settlementRow
                        ? `${amount(anomalyDetailQuery.data.settlementRow.amountSatang)} ${anomalyDetailQuery.data.settlementRow.currency}`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ยอดที่คาดหมาย</div>
                    <div>
                      {anomalyDetailQuery.data.match.expectedAmountSatang ===
                      null
                        ? '—'
                        : `${amount(anomalyDetailQuery.data.match.expectedAmountSatang)} ${anomalyDetailQuery.data.match.currency ?? '—'}`}
                    </div>
                  </div>
                  <div>
                    <div className="text-ink-3">ส่วนต่าง</div>
                    <div className="font-semibold tabular-nums">
                      {anomalyDetailQuery.data.settlementRow &&
                      anomalyDetailQuery.data.match.expectedAmountSatang !==
                        null
                        ? `${signedAmount(
                            anomalyDetailQuery.data.settlementRow.amountSatang -
                              anomalyDetailQuery.data.match
                                .expectedAmountSatang,
                          )} ${anomalyDetailQuery.data.settlementRow.currency}`
                        : '—'}
                    </div>
                  </div>
                </div>
                <h4 className="font-bold">สรุปรายการชำระที่อาจตรงกัน</h4>
                <p className="mt-2 text-sm-label text-ink-3">
                  พบ {anomalyDetailQuery.data.match.candidateEvidence.total}{' '}
                  รายการ
                  {anomalyDetailQuery.data.match.candidateEvidence.truncated
                    ? ' (จำนวนถูกจำกัดในรายละเอียด)'
                    : ''}
                </p>
              </div>
            ) : null}
            {anomalyDetailQuery.data.alert.status === 'open' ? (
              <div className="grid gap-3 border-t border-line-2 pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <Field label="เหตุผลในการปิดการแจ้งเตือน">
                  <Input
                    aria-label={`เหตุผลการปิดการแจ้งเตือนจากไฟล์ ${anomalyDetailQuery.data.alert.alertId}`}
                    maxLength={500}
                    value={
                      anomalyReasons[anomalyDetailQuery.data.alert.alertId] ??
                      ''
                    }
                    onChange={(event) =>
                      setAnomalyReasons((current) => ({
                        ...current,
                        [anomalyDetailQuery.data!.alert.alertId]:
                          event.target.value,
                      }))
                    }
                    placeholder="ระบุเหตุผลที่ตรวจสอบได้"
                  />
                </Field>
                <Btn
                  kind="primary"
                  className="w-full sm:w-auto"
                  disabled={
                    !anomalyReasons[
                      anomalyDetailQuery.data.alert.alertId
                    ]?.trim() || anomalyResolveMutation.isPending
                  }
                  onClick={() =>
                    anomalyResolveMutation.mutate({
                      alertId: anomalyDetailQuery.data!.alert.alertId,
                      reason:
                        anomalyReasons[
                          anomalyDetailQuery.data!.alert.alertId
                        ]!.trim(),
                    })
                  }
                >
                  ปิดการแจ้งเตือน
                </Btn>
              </div>
            ) : null}
            {anomalyResolveMutation.isError ? (
              <p className="text-danger">
                ปิดการแจ้งเตือน ไม่สำเร็จ:{' '}
                {errorMessage(anomalyResolveMutation.error)}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
